import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import oauthApi from '../services/oauth';
import { toast } from 'react-toastify';

export const useOAuth = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const clientId = searchParams.get('client_id');
  const redirectUri = searchParams.get('redirect_uri');
  const scopes = searchParams.get('scopes')?.split(',') || [
    'cameras_user_info',
  ];
  const authCode = searchParams.get('auth_code');

  useEffect(() => {
    if (!clientId || !redirectUri) {
      toast.error('Missing required parameters');
      navigate('/login');
    }
  }, [clientId, redirectUri, navigate]);

  const handleAuthorize = async (selectedScopes: string[]) => {
    try {
      setIsLoading(true);
      const { data } = await oauthApi.authorize({
        client_id: clientId!,
        redirect_uri: redirectUri!,
        scopes: [...selectedScopes],
      });

      if (data.auth_code) {
        const redirectUrl = new URL(redirectUri!);
        // Preserve existing query params
        searchParams.forEach((value, key) => {
          if (key !== 'auth_code') {
            // Don't overwrite the new auth_code
            redirectUrl.searchParams.set(key, value);
          }
        });
        // Add the auth_code
        redirectUrl.searchParams.set('auth_code', data.auth_code);
        window.location.href = redirectUrl.toString();
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Authorization failed');
      throw new Error('Authorization failed');
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    clientId,
    redirectUri,
    scopes,
    authCode,
    handleAuthorize,
  };
};
